import type { PointerEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

type ImageLightboxProps = {
  open: boolean;
  src: string;
  alt: string;
  onClose: () => void;
};

export function ImageLightbox({ open, src, alt, onClose }: ImageLightboxProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef<{ active: boolean; startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  const canRender = typeof document !== "undefined";

  useEffect(() => {
    if (!open) {
      return;
    }

    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [open, src]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, onClose]);

  const zoomIn = () => setScale((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))));
  const zoomOut = () =>
    setScale((prev) => {
      const next = Math.max(1, Number((prev - 0.25).toFixed(2)));
      if (next === 1) {
        setOffset({ x: 0, y: 0 });
      }
      return next;
    });

  const reset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (scale <= 1) {
      return;
    }

    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    dragState.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = dragState.current;
    if (!state?.active) {
      return;
    }

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setOffset({ x: state.baseX + dx, y: state.baseY + dy });
  };

  const onPointerUp = () => {
    if (!dragState.current) {
      return;
    }
    dragState.current.active = false;
  };

  const content = useMemo(() => {
    if (!open || !canRender) {
      return null;
    }

    return (
      <div
        className="fixed inset-0 z-[2147483647] bg-black/85 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-label={alt}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="absolute top-4 right-4 left-4 z-20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={zoomOut}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white border border-white/20"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={zoomIn}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white border border-white/20"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white border border-white/20"
              aria-label="Reset"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white border border-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          ref={containerRef}
          className="absolute inset-0 z-10 flex items-center justify-center p-6 pt-20"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <div
            className="max-h-full max-w-full touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{ cursor: scale > 1 ? "grab" : "default" }}
          >
            <img
              src={src}
              alt={alt}
              draggable={false}
              className="max-h-[85vh] max-w-[95vw] select-none"
              style={{
                transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
                transformOrigin: "center center",
              }}
            />
          </div>
        </div>
      </div>
    );
  }, [alt, canRender, offset.x, offset.y, onClose, open, scale, src]);

  if (!content) {
    return null;
  }

  return createPortal(content, document.body);
}
